import {
  Box,
  Button,
  Divider,
  Group,
  Loader,
  Stack,
  TagsInput,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { IconBrandTelegram, IconCheck } from '@tabler/icons-react';
import type { Editor } from '@tiptap/react';
import { useGetDebtor } from 'api';
import { RichTextArea } from 'components/RichTextArea';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { useSaveDraft } from 'modules/Mails/pages/MailDetail/hooks/use-save-draft';
import { DebtorFileInsert } from 'modules/Mails/pages/NewMail/components/DebtorFileInsert';
import { useNewMailForm } from 'modules/Mails/pages/NewMail/hooks';
import posthog from 'posthog-js';
import { useEffect, useRef, useState } from 'react';
import { isNonNullish } from 'remeda';
import { captureEvent } from 'shared/analytics/capture-event';
import { useLoadExternalMailboxes } from 'shared/hooks/use-load-external-mailboxes';

type NewMailProps = {
  debtorMail?: string;
  debtorId?: string;
  onSent?: () => void;
};

export const NewMail = ({ debtorMail, debtorId, onSent }: NewMailProps) => {
  const { t } = useTranslation();
  const company = useCompanyConfiguration();

  const editorRef = useRef<Editor | null>(null);

  useEffect(() => {
    captureEvent(posthog, 'mail:composer_opened', { source: 'new_mail', trigger: 'button' });
  }, []);

  const [showCc, setShowCc] = useState(false);
  const [showCci, setShowCci] = useState(false);
  const [defaultBody, setDefaultBody] = useState('');

  const { externalMailboxes } = useLoadExternalMailboxes();
  const { form, handleSend, isSendEmailLoading, setAttachments, initialBody, draft, setDraft } =
    useNewMailForm({ source: 'new_mail', debtorMail, debtorId, onSuccess: onSent });

  const { isSavedAsDraft, isSaveDraftLoading } = useSaveDraft({
    source: 'new_mail',
    threadId: draft?.threadId,
    draftMailId: draft?.id ?? null,
    formValues: form.values,
    defaultBody,
    isSending: isSendEmailLoading,
    onDraftCreated: setDraft,
    onDraftMissing: () => setDraft(null),
  });

  const { data: debtorResponse } = useGetDebtor(debtorId ?? '', undefined, {
    query: { enabled: isNonNullish(debtorId) },
  });
  const debtorEmailSuggestions = debtorResponse?.data.attributes.emails ?? [];

  const mailboxes = externalMailboxes.data.filter(
    m => company.custom_domain && m.attributes.login?.includes(company.custom_domain),
  );

  const userSignature = mailboxes[0]?.attributes?.signature ?? '';

  return (
    <form onSubmit={handleSend}>
      <Stack mb="lg" gap={4}>
        <Group gap={0} align="center">
          <TagsInput
            variant="unstyled"
            placeholder={t(i18nKeys.EXTERNAL_MAIL.RECIPIENTS_PLACEHOLDER)}
            style={{ flex: 1 }}
            data={debtorEmailSuggestions}
            limit={5}
            {...form.getInputProps('toMail')}
          />
          <Group gap={8} pr={8}>
            {!showCc && (
              <Text
                size="xs"
                c="dimmed"
                style={{ cursor: 'pointer' }}
                onClick={() => setShowCc(true)}
              >
                {t(i18nKeys.EXTERNAL_MAIL.CC)}
              </Text>
            )}
            {!showCci && (
              <Text
                size="xs"
                c="dimmed"
                style={{ cursor: 'pointer' }}
                onClick={() => setShowCci(true)}
              >
                {t(i18nKeys.EXTERNAL_MAIL.BCC)}
              </Text>
            )}
          </Group>
        </Group>
        <Divider />
        {showCc && (
          <>
            <TagsInput
              variant="unstyled"
              {...form.getInputProps('ccMail')}
              placeholder={t(i18nKeys.EXTERNAL_MAIL.CC)}
              data={debtorEmailSuggestions}
            />
            <Divider />
          </>
        )}
        {showCci && (
          <>
            <TagsInput
              variant="unstyled"
              {...form.getInputProps('bccMail')}
              placeholder={t(i18nKeys.EXTERNAL_MAIL.BCC)}
              data={debtorEmailSuggestions}
            />
            <Divider />
          </>
        )}
        <TextInput
          variant="unstyled"
          placeholder={t(i18nKeys.SUBJECT)}
          {...form.getInputProps('subject')}
        />
        <Divider />
      </Stack>
      <RichTextArea
        content={initialBody ?? (userSignature ? `<br/><br/>${userSignature}` : '')}
        onChange={value => form.setFieldValue('body', value)}
        onEditorReady={editor => {
          editorRef.current = editor;
          const html = editor.getHTML();
          form.setFieldValue('body', html);
          setDefaultBody(html);
        }}
        onFileChange={setAttachments}
        variant="email"
        styles={{ content: { minHeight: 300 } }}
        toolbarActions={
          isNonNullish(debtorId) && <DebtorFileInsert debtorId={debtorId} editorRef={editorRef} />
        }
      />
      {form.errors.body && (
        <Text c="red" size="xs" mt={4}>
          {form.errors.body}
        </Text>
      )}
      <Group justify="flex-end" mt="md">
        {isSaveDraftLoading ? (
          <Loader size={14} />
        ) : isSavedAsDraft ? (
          <Tooltip label={t(i18nKeys.EXTERNAL_MAIL.DETAIL_NEXT.SAVED_AS_DRAFT)}>
            <Box display="flex">
              <IconCheck size={14} color="var(--mantine-color-green-6)" />
            </Box>
          </Tooltip>
        ) : null}
        <Button
          type="submit"
          loading={isSendEmailLoading}
          leftSection={<IconBrandTelegram size={18} />}
        >
          {t(i18nKeys.SUBMIT)}
        </Button>
      </Group>
    </form>
  );
};
